<template>
  <div>
    <!-- Desktop right-click overlay -->
    <div v-if="open && contextMode && !isMobile" class="context-overlay" @click="close" @contextmenu.prevent="close" />

    <!-- Code copy mini-menu -->
    <div v-if="codeCopyMenu.show" class="context-overlay" @click="closeCodeCopyMenu" @contextmenu.prevent="closeCodeCopyMenu" />
    <transition name="fan-card">
      <div v-if="codeCopyMenu.show" class="fan-card" :style="{ top: codeCopyMenu.y + 'px', left: codeCopyMenu.x + 'px' }">
        <div class="fan-item" @click="doCopyCode">
          <span class="fan-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg></span>
          <span class="fan-item-label">{{ $t('copy_code') || '复制' }}</span>
        </div>
      </div>
    </transition>

    <!-- Menu card -->
    <transition name="fan-card" @after-leave="onClosed">
      <div v-if="open" class="fan-card" :class="{ mobile: isMobile }" :style="cardStyle"
        @mouseenter="onCardEnter" @mouseleave="onCardLeave">
        <div
          v-for="(item, index) in visibleItems"
          :key="item.key"
          class="fan-item"
          :style="{ transitionDelay: `${index * 0.03}s` }"
          @click="item.action"
          @touchstart="onItemTouchStart"
          @touchend="onItemTouchEnd"
          @touchcancel="onItemTouchEnd"
        >
          <span class="fan-item-icon" v-html="item.icon" />
          <span class="fan-item-label">{{ item.label }}</span>
        </div>
      </div>
    </transition>

    <!-- ===== Button Group (MCB + BHB) ===== -->
    <div
      class="btn-group"
      :class="{ dragging }"
      :style="groupStyle"
      ref="btnGroup"
    >
      <!-- BHB -->
      <transition name="bhb">
        <button v-if="isMobile && showBHB" class="bhb-btn"
          @touchstart="onGroupTouchStart($event)"
          @touchend="onGroupTouchEnd"
          @touchmove="onGroupTouchMove($event)"
          @touchcancel="onGroupTouchEnd"
          @mousedown="isMobile && onGroupMouseDown($event)"
          @mouseup="isMobile && onGroupMouseUp()"
          @click="onBHBClick"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path v-if="isPostDetail" d="M19 12H5M12 19l-7-7 7-7"/>
            <template v-else>
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </template>
          </svg>
        </button>
      </transition>

      <!-- MCB reveal zone (PC hidden state) -->
      <div v-if="!isMobile && mcbHidden" class="mcb-reveal-zone"
        @mouseenter="revealMCB" />

      <!-- MCB -->
      <button class="menu-toggle" :class="{ open, hidden: !isMobile && mcbHidden }"
        @touchstart="onGroupTouchStart($event)"
        @touchend="onGroupTouchEnd"
        @touchmove="onGroupTouchMove($event)"
        @touchcancel="onGroupTouchEnd"
        @mousedown="isMobile && onGroupMouseDown($event)"
        @mouseup="isMobile && onGroupMouseUp()"
        @mouseenter="!isMobile && !mcbHidden && onBtnEnter()"
        @mouseleave="!isMobile && !mcbHidden && onBtnLeave()"
        @click="onMCBClick"
      >
        <span class="menu-icon-bar" />
        <span class="menu-icon-bar" />
        <span class="menu-icon-bar" />
      </button>
    </div>

    <!-- Logout confirm modal -->
    <div v-if="showLogout" class="modal-overlay" @click.self="showLogout = false">
      <div class="modal-box">
        <div class="modal-title">{{ $t('delete_account') }}</div>
        <p class="text-secondary text-sm mb-2">{{ $t('delete_account_confirm') }}</p>
        <input v-model="logoutPassword" type="password" class="input-field" :placeholder="$t('password')" />
        <p v-if="logoutError" class="text-sm mb-1" style="color:#d32f2f">{{ logoutError }}</p>
        <div class="modal-actions mt-2">
          <button class="btn" @click="showLogout = false">{{ $t('cancel') }}</button>
          <button class="btn btn-danger" @click="confirmLogout" :disabled="loggingOut">{{ loggingOut ? '...' : $t('delete_account') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FloatingMenu',
  data() {
    return {
      open: false,
      contextMode: false,
      cardX: 0, cardY: 0,
      theme: 'light',
      showLogout: false,
      logoutPassword: '', logoutError: '', loggingOut: false,
      closeTimer: null,
      mcbHidden: false,
      codeCopyMenu: { show: false, x: 0, y: 0, pre: null },
      _version: 0,
      windowW: window.innerWidth,
      windowH: window.innerHeight,

      // Drag state (mobile only)
      dragging: false,
      groupX: null,   // null = use CSS default
      groupY: null,
      longPressTimer: null,
      longPressFired: false,
      dragOffsetX: 0,  // offset from touch point to group top-left
      dragOffsetY: 0,
      dragStartX: 0, dragStartY: 0,
      groupW: 44,      // button group width (assume 44px, updated on mount)
      groupH: 44       // height of MCB + BHB gap + BHB
    }
  },
  watch: {
    '$route'() { this._version++; this.close() }
  },
  computed: {
    isMobile() {
      const ua = navigator.userAgent.toLowerCase()
      const isMobileOS = /android|harmonyos|iphone|ipad|ios|mobile/.test(ua)
      const narrow = this.windowW < 768 && this.windowH > this.windowW
      return isMobileOS && narrow
    },
    routePath() { return this.$route.path },
    isPostDetail() { return this.routePath.startsWith('/blog/') },
    showBHB() {
      return this.routePath !== '/'
    },
    bhbAction() {
      if (this.isPostDetail) return () => { this.close(); this.$router.back() }
      return () => { this.close(); this.$router.push('/') }
    },
    isLoggedIn() { void this._version; return !!localStorage.getItem('markjy_token') },
    isAdmin() { void this._version; return localStorage.getItem('markjy_is_admin') === 'true' },
    langLabel() {
      const state = this.$i18nState
      const labels = { 'zh-CN': '简', 'zh-TW': '繁', 'en': 'EN' }
      return state ? (labels[state.lang] || '简') : '简'
    },
    cardStyle() {
      if (this.contextMode) {
        const maxX = this.windowW - 180; const maxY = this.windowH - 300
        return { top: Math.min(this.cardY, maxY) + 'px', left: Math.min(this.cardX, maxX) + 'px' }
      }
      const gap = 8; const cardW = 170
      const cardH = Math.min(this.visibleItems.length * 42 + 8, 400)

      // Button group geometry
      let gLeft, gTop, gW, gH
      if (this.groupX != null) {
        gLeft = this.groupX; gTop = this.groupY; gW = this.groupW; gH = this.groupH
      } else if (this.isMobile) {
        gW = 44; gH = this.showBHB ? 96 : 44
        gLeft = this.windowW - 36 - gW; gTop = this.windowH - 36 - gH
      } else {
        gW = 44; gH = 44
        gLeft = 36; gTop = this.windowH - 36 - gH
      }
      const gRight = gLeft + gW; const gBottom = gTop + gH

      // Available space in each quadrant
      const spaceTop = gTop - gap
      const spaceBottom = this.windowH - gBottom - gap
      const spaceLeft = gLeft
      const spaceRight = this.windowW - gRight

      // Order quadrants by button position: prefer the opposite quadrant (most space)
      const midW = this.windowW / 2; const midH = this.windowH / 2
      const btnNearLeft = gLeft + gW / 2 < midW
      const btnNearTop = gTop + gH / 2 < midH
      let quadrants
      if (btnNearLeft && btnNearTop) {
        quadrants = ['br', 'tr', 'bl', 'tl']       // top-left button → prefer bottom-right
      } else if (!btnNearLeft && btnNearTop) {
        quadrants = ['bl', 'tl', 'br', 'tr']       // top-right button → prefer bottom-left
      } else if (btnNearLeft && !btnNearTop) {
        quadrants = ['tr', 'br', 'tl', 'bl']       // bottom-left button → prefer top-right
      } else {
        quadrants = ['tl', 'tr', 'bl', 'br']       // bottom-right button → prefer top-left
      }

      // Pick first quadrant that fits both axes; fallback: best single-axis match
      const fits = {
        tl: spaceTop >= cardH && spaceLeft >= cardW,
        tr: spaceTop >= cardH && spaceRight >= cardW,
        bl: spaceBottom >= cardH && spaceLeft >= cardW,
        br: spaceBottom >= cardH && spaceRight >= cardW
      }
      let pick = null
      for (const q of quadrants) { if (fits[q]) { pick = q; break } }
      if (!pick) {
        const v = spaceTop >= spaceBottom ? 't' : 'b'
        const h = spaceLeft >= spaceRight ? 'l' : 'r'
        pick = v + h
      }

      const style = {}
      if (pick[0] === 't') {
        style.bottom = (this.windowH - gTop + gap) + 'px'
      } else {
        style.top = (gBottom + gap) + 'px'
      }
      // Horizontal: align card edge with button edge on the same side
      if (gLeft + gW / 2 > this.windowW / 2) {
        style.right = (this.windowW - gRight) + 'px'   // button on right → align right
      } else {
        style.left = gLeft + 'px'                        // button on left → align left
      }
      return style
    },
    groupStyle() {
      const t = 'left 0.35s cubic-bezier(0.22, 0.61, 0.36, 1), top 0.35s cubic-bezier(0.22, 0.61, 0.36, 1)'
      const style = { transition: t }
      if (this.dragging) {
        style.left = this.groupX + 'px'; style.top = this.groupY + 'px'
        style.transition = 'none'
        return style
      }
      if (this.groupX != null && this.groupY != null) {
        style.left = this.groupX + 'px'; style.top = this.groupY + 'px'
        return style
      }
      if (this.isMobile) { style.right = '36px'; style.bottom = '36px' }
      else { style.left = '36px'; style.bottom = '36px' }
      return style
    },
    visibleItems() {
      if (this.isMobile) {
        const items = [
          { key: 'lang', label: this.langLabel,
            icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
            action: () => this.cycleLang() },
          { key: 'theme', label: this.theme === 'light' ? this.$t('theme_dark') : this.$t('theme_light'),
            icon: this.theme === 'light'
              ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/></svg>'
              : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>',
            action: () => this.toggleTheme() }
        ]
        if (!this.isLoggedIn) {
          items.push({ key: 'login', label: this.$t('login'),
            icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>',
            action: () => this.navigate('/login') })
        }
        if (this.isLoggedIn && this.isAdmin) {
          items.push({ key: 'admin', label: this.$t('admin'),
            icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>',
            action: () => this.navigate('/admin') })
        }
        if (this.isLoggedIn) {
          items.push({ key: 'signout', label: this.$t('signout'),
            icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>',
            action: () => this.handleSignOut() })
          items.push({ key: 'logout', label: this.$t('logout'),
            icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>',
            action: () => this.handleLogout() })
        }
        return items
      }
      const items = [
        { key: 'home', label: this.$t('home'),
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>',
          action: () => this.navigate('/') },
        { key: 'blog', label: this.$t('blog'),
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>',
          action: () => this.navigate('/blog') },
        { key: 'about', label: this.$t('about'),
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
          action: () => this.navigate('/about') },
        { key: 'contact', label: this.$t('contact'),
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>',
          action: () => this.navigate('/contact') },
        { key: 'lang', label: this.langLabel,
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
          action: () => this.cycleLang() },
        { key: 'theme', label: this.theme === 'light' ? this.$t('theme_dark') : this.$t('theme_light'),
          icon: this.theme === 'light'
            ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/></svg>'
            : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>',
          action: () => this.toggleTheme() }
      ]
      if (!this.isLoggedIn) {
        items.push({ key: 'login', label: this.$t('login'),
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>',
          action: () => this.navigate('/login') })
      }
      if (this.isLoggedIn && this.isAdmin) {
        items.push({ key: 'admin', label: this.$t('admin'),
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>',
          action: () => this.navigate('/admin') })
      }
      if (this.isLoggedIn) {
        items.push({ key: 'signout', label: this.$t('signout'),
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>',
          action: () => this.handleSignOut() })
        items.push({ key: 'logout', label: this.$t('logout'),
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>',
          action: () => this.handleLogout() })
      }
      return items
    }
  },
  methods: {
    onMCBClick() {
      if (this.longPressFired) { this.longPressFired = false; return }
      // PC: if menu is open, hide MCB instead of closing menu
      if (!this.isMobile && this.open) {
        this.mcbHidden = true
        this.close()
        return
      }
      if (this.open) { this.close(); return }
      this.contextMode = false; this.open = true
    },
    revealMCB() {
      this.mcbHidden = false
    },
    showCodeCopyMenu(x, y, pre) {
      this.close()
      this.codeCopyMenu = { show: true, x, y, pre }
    },
    closeCodeCopyMenu() {
      this.codeCopyMenu = { show: false, x: 0, y: 0, pre: null }
    },
    doCopyCode() {
      const pre = this.codeCopyMenu.pre
      if (pre) {
        const code = pre.querySelector('code') || pre
        navigator.clipboard.writeText(code.textContent || '').catch(() => {})
      }
      this.closeCodeCopyMenu()
    },
    onBHBClick() {
      if (this.longPressFired) { this.longPressFired = false; return }
      this.bhbAction()
    },

    // ===== Long press + drag (mobile only) =====
    onGroupTouchStart(e) {
      if (!this.isMobile) return
      if (this.open) return
      this.longPressFired = false
      this.dragStartX = e.touches[0].clientX
      this.dragStartY = e.touches[0].clientY
      this.startLongPress()
    },
    onGroupTouchMove(e) {
      if (this.dragging) {
        e.preventDefault()  // prevent scroll only while dragging
        this.updateDragPosition(e.touches[0].clientX, e.touches[0].clientY)
        return
      }
      if (!this.longPressTimer) return
      const dx = Math.abs(e.touches[0].clientX - this.dragStartX)
      const dy = Math.abs(e.touches[0].clientY - this.dragStartY)
      if (dx > 6 || dy > 6) this.clearLongPress()
    },
    onGroupTouchEnd() {
      this.clearLongPress()
    },
    onGroupMouseDown(e) {
      if (!this.isMobile) return
      if (this.open) return
      this.longPressFired = false
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.startLongPress()
    },
    onGroupMouseUp() {
      this.clearLongPress()
    },
    startLongPress() {
      this.clearLongPress()
      this.longPressTimer = setTimeout(() => {
        this.longPressFired = true
        this.enterDrag(this.dragStartX, this.dragStartY)
      }, 1000)
    },
    clearLongPress() {
      if (this.longPressTimer) { clearTimeout(this.longPressTimer); this.longPressTimer = null }
    },
    enterDrag(clientX, clientY) {
      this.dragging = true
      const el = this.$refs.btnGroup
      if (!el) return
      const rect = el.getBoundingClientRect()
      this.groupW = rect.width
      this.groupH = rect.height
      this.groupX = rect.left
      this.groupY = rect.top
      this.dragOffsetX = clientX - rect.left
      this.dragOffsetY = clientY - rect.top
      document.addEventListener('touchmove', this.onDragMove, { passive: false })
      document.addEventListener('touchend', this.onDragEnd)
      document.addEventListener('touchcancel', this.onDragEnd)
      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup', this.onDragEnd)
    },
    updateDragPosition(clientX, clientY) {
      const inset = 25
      let nx = clientX - this.dragOffsetX
      let ny = clientY - this.dragOffsetY
      nx = Math.max(inset, Math.min(this.windowW - this.groupW - inset, nx))
      ny = Math.max(inset, Math.min(this.windowH - this.groupH - inset, ny))
      this.groupX = nx
      this.groupY = ny
    },
    onDragMove(e) {
      if (!this.dragging) return
      e.preventDefault()
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY
      this.updateDragPosition(clientX, clientY)
    },
    onDragEnd() {
      document.removeEventListener('touchmove', this.onDragMove)
      document.removeEventListener('touchend', this.onDragEnd)
      document.removeEventListener('touchcancel', this.onDragEnd)
      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup', this.onDragEnd)
      this.dragging = false
      if (this.groupX == null || this.groupY == null) return
      // Snap to nearest edge or corner
      const inset = 25
      const cornerThresh = 15
      const cx = this.groupX + this.groupW / 2
      const cy = this.groupY + this.groupH / 2
      const distL = cx - inset
      const distR = (this.windowW - inset) - cx
      const distT = cy - inset
      const distB = (this.windowH - inset) - cy
      let snapX, snapY
      // Corner detection: within cornerThresh of both edges → snap to corner
      if (distL < cornerThresh && distT < cornerThresh) { snapX = inset; snapY = inset }
      else if (distR < cornerThresh && distT < cornerThresh) { snapX = this.windowW - this.groupW - inset; snapY = inset }
      else if (distL < cornerThresh && distB < cornerThresh) { snapX = inset; snapY = this.windowH - this.groupH - inset }
      else if (distR < cornerThresh && distB < cornerThresh) { snapX = this.windowW - this.groupW - inset; snapY = this.windowH - this.groupH - inset }
      else {
        // Snap to nearest single edge
        const minH = Math.min(distL, distR)
        const minV = Math.min(distT, distB)
        snapX = minH <= minV ? (distL <= distR ? inset : this.windowW - this.groupW - inset) : cx - this.groupW / 2
        snapY = minH <= minV ? cy - this.groupH / 2 : (distT <= distB ? inset : this.windowH - this.groupH - inset)
      }
      // Clamp
      this.groupX = Math.max(inset, Math.min(this.windowW - this.groupW - inset, snapX))
      this.groupY = Math.max(inset, Math.min(this.windowH - this.groupH - inset, snapY))
      this.clearLongPress()
      setTimeout(() => { this.longPressFired = false }, 300)
    },
    exitDrag() {
      this.dragging = false
      this.clearLongPress()
      document.removeEventListener('touchmove', this.onDragMove)
      document.removeEventListener('touchend', this.onDragEnd)
      document.removeEventListener('touchcancel', this.onDragEnd)
      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup', this.onDragEnd)
    },

    toggle() { /* legacy*/ },
    close() { this.open = false; this.contextMode = false; this.clearTimer() },
    onClosed() {},
    onBtnEnter() { this.clearTimer(); this.contextMode = false; this.open = true },
    onBtnLeave() { this.scheduleClose() },
    onCardEnter() { this.clearTimer() },
    onCardLeave() { if (!this.contextMode) this.scheduleClose() },
    scheduleClose() { this.clearTimer(); this.closeTimer = setTimeout(() => { this.close() }, 250) },
    clearTimer() { if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null } },
    onItemTouchStart(e) { e.currentTarget.classList.add('fan-item-pressed') },
    onItemTouchEnd(e) { e.currentTarget.classList.remove('fan-item-pressed') },
    onContextMenu(e) {
      if (this.isMobile) return
      if (e.target.closest('.btn-group') || e.target.closest('.fan-card')) { e.preventDefault(); return }
      // Don't intercept if text is selected within article content
      const sel = window.getSelection()
      if (sel && sel.toString().trim().length > 0) {
        const inContent = e.target.closest('.post-content') || e.target.closest('.markdown-body') || e.target.closest('.about-content')
        if (inContent) return
      }
      // Right-click on code block: show copy-only mini menu
      const pre = e.target.closest('pre')
      if (pre) {
        e.preventDefault()
        this.showCodeCopyMenu(e.clientX, e.clientY, pre)
        return
      }
      e.preventDefault()
      this.clearTimer(); this.contextMode = true
      this.cardX = e.clientX; this.cardY = e.clientY; this.open = true
    },
    navigate(path) { this.close(); if (this.$route.path !== path) this.$router.push(path) },
    toggleTheme() {
      this.theme = this.theme === 'light' ? 'dark' : 'light'
      document.documentElement.setAttribute('data-theme', this.theme)
      localStorage.setItem('markjy_theme', this.theme)
    },
    cycleLang() {
      const state = this.$i18nState; const langs = ['zh-CN', 'zh-TW', 'en']
      const idx = state ? langs.indexOf(state.lang) : 0
      this.$setLang(langs[(idx + 1) % 3])
    },
    handleSignOut() {
      this.close()
      ;['token','username','is_admin','user_id'].forEach(k => localStorage.removeItem('markjy_'+k))
      this._version++
      if (this.$route.path === '/admin') this.$router.push('/')
    },
    handleLogout() { this.showLogout = true; this.logoutPassword = ''; this.logoutError = '' },
    confirmLogout() {
      if (!this.logoutPassword) { this.logoutError = '请输入密码'; return }
      this.loggingOut = true
      import('../api').then(({ default: api }) => {
        api.post('/auth/login', {
          username: localStorage.getItem('markjy_username'), password: this.logoutPassword
        }).then(() => this.doLogout()).catch(() => { this.logoutError = '密码错误'; this.loggingOut = false })
      })
    },
    doLogout() {
      ['token','username','is_admin','user_id'].forEach(k => localStorage.removeItem('markjy_'+k))
      this._version++
      this.showLogout = false; this.logoutPassword = ''; this.loggingOut = false; this.close()
      if (this.$route.path === '/admin') this.$router.push('/')
    },
    onWindowResize() {
      this.windowW = window.innerWidth; this.windowH = window.innerHeight
    }
  },
  mounted() {
    this.theme = localStorage.getItem('markjy_theme') ||
      (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    document.documentElement.setAttribute('data-theme', this.theme)
    if (!this.isMobile) {
      document.addEventListener('contextmenu', this.onContextMenu)
    }
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy() {
    document.removeEventListener('contextmenu', this.onContextMenu)
    window.removeEventListener('resize', this.onWindowResize)
    this.clearTimer(); this.clearLongPress(); this.exitDrag()
  }
}
</script>

<style scoped>
/* ===== Button Group ===== */
.btn-group {
  position: fixed;
  z-index: 1002;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}
.btn-group.dragging {
  z-index: 2001;
}

/* ===== MCB ===== */
.menu-toggle {
  width: 44px; height: 44px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 4px;
  transition: background 0.1s ease-out, box-shadow 0.25s ease;
  flex-shrink: 0;
}
.menu-toggle:hover { background: var(--hover-bg); }
.menu-toggle.open { background: var(--bg-secondary); }
.menu-toggle.hidden {
  transform: translateX(calc(-100% - 30px));
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
.menu-toggle:not(.hidden) {
  transition: transform 0.35s cubic-bezier(0.22, 0.61, 0.36, 1);
}
/* Reveal zone: 20px to the right of hidden MCB (2px visible edge + 20px margin) */
.mcb-reveal-zone {
  position: fixed;
  left: 2px;
  bottom: 36px;
  width: 48px;
  height: 44px;
  z-index: 1003;
}
.menu-icon-bar {
  display: block; width: 20px; height: 1.5px;
  background: var(--text-primary); border-radius: 1px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.open .menu-icon-bar:nth-child(1) { transform: translateY(5.5px) rotate(45deg); }
.open .menu-icon-bar:nth-child(2) { opacity: 0; transform: scaleX(0); }
.open .menu-icon-bar:nth-child(3) { transform: translateY(-5.5px) rotate(-45deg); }

/* Drag glow/shadow */
.btn-group.dragging .menu-toggle {
  box-shadow: 0 0 24px rgba(0,0,0,0.18), 0 0 60px rgba(0,0,0,0.08);
}
[data-theme="dark"] .btn-group.dragging .menu-toggle {
  box-shadow: 0 0 24px rgba(255,255,255,0.15), 0 0 60px rgba(255,255,255,0.06);
}

/* ===== BHB ===== */
.bhb-btn {
  width: 44px; height: 44px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: background 0.1s ease-out, box-shadow 0.25s ease;
}
.bhb-btn:hover, .bhb-btn:active { background: var(--hover-bg); }
.dragging .bhb-btn {
  box-shadow: 0 0 24px rgba(0,0,0,0.18), 0 0 60px rgba(0,0,0,0.08);
}
[data-theme="dark"] .dragging .bhb-btn {
  box-shadow: 0 0 24px rgba(255,255,255,0.15), 0 0 60px rgba(255,255,255,0.06);
}

/* BHB transition */
.bhb-enter-active { transition: all 0.28s cubic-bezier(0.22, 0.61, 0.36, 1); }
.bhb-leave-active { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
.bhb-enter { opacity: 0; transform: translateY(12px); }
.bhb-leave-to { opacity: 0; transform: translateY(12px); }

/* ===== Menu card ===== */
.fan-card {
  position: fixed;
  width: 170px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0;
  display: flex; flex-direction: column;
  overflow: hidden;
  z-index: 1001;
}
/* Items */
.fan-item {
  display: flex; align-items: center; gap: 10px;
  padding: 11px 16px;
  cursor: pointer;
  transition: background 0.08s ease-out;
  border-bottom: 1px solid var(--border-light);
  opacity: 0;
  animation: fanItemIn 0.25s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
}
.fan-item:first-child { border-radius: 10px 10px 0 0; }
.fan-item:last-child { border-bottom: none; border-radius: 0 0 10px 10px; }
.fan-item:hover { background: var(--hover-bg); }
.fan-item-pressed { background: var(--hover-bg) !important; }
.fan-item-icon { display: flex; align-items: center; flex-shrink: 0; opacity: 0.5; color: var(--text-primary); }
.fan-item-label { font-size: 0.8rem; color: var(--text-primary); white-space: nowrap; }

@keyframes fanItemIn {
  from { opacity: 0; transform: translateX(8px); }
  to   { opacity: 1; transform: translateX(0); }
}

.fan-card-enter-active { transition: all 0.25s cubic-bezier(0.22, 0.61, 0.36, 1); }
.fan-card-leave-active { transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1); }
.fan-card-enter { opacity: 0; transform: translateY(6px); }
.fan-card-leave-to { opacity: 0; transform: translateY(4px); }

.context-overlay { position: fixed; inset: 0; z-index: 998; background: transparent; }
.modal-overlay { position: fixed; inset: 0; background: var(--overlay); z-index: 2000; display: flex; align-items: center; justify-content: center; }
.modal-box { background: var(--bg-primary); border: 1px solid var(--border-color); border-radius: 12px; padding: 24px; max-width: 380px; width: 90%; box-shadow: var(--shadow-lg); }
.modal-title { font-size: 1.05rem; font-weight: 600; margin-bottom: 8px; }
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; }
</style>
